/**
 * AdminDisputes.jsx — Admin dispute management portal.
 *
 * Tabs:
 *   0 — All Disputes
 *   1 — Escalated Disputes
 *   2 — Active Arbitration Rooms
 *   3 — Closed Arbitration Rooms
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, CircularProgress, Collapse, Divider,
  IconButton, MenuItem, Paper, Select, Tab, Tabs, Table, TableBody,
  TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
  Dialog, DialogTitle, DialogContent, DialogActions, InputLabel, FormControl, TableContainer,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import ExtensionIcon from '@mui/icons-material/Extension';
import GavelIcon from '@mui/icons-material/Gavel';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import { apiClient } from '../../utils/auth';

const DISPUTE_STATUS_COLORS = {
  OPEN: 'warning',
  RESOLVED_BUYER: 'success',
  RESOLVED_BIDDER: 'success',
  ESCALATED: 'error',
  DISMISSED: 'default',
};

const ROOM_STATUS_COLORS = { ACTIVE: 'error', CLOSED: 'default', RESOLVED: 'success' };
const ROLE_COLORS = { BUYER: '#1976d2', BIDDER: '#388e3c', ADMIN: '#7b1fa2' };

// ─── Reusable chat thread component (unchanged from original) ───────────────
function AdminChatThread({ disputeId, onSent }) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [chatInfo, setChatInfo] = useState({});
  const [loading, setLoading] = useState(true);
  const bottomRef = useRef(null);

  const fetchMessages = useCallback(async () => {
    try {
      const { data } = await apiClient.get(`/api/v1/disputes/${disputeId}/chat/`);
      setMessages(data.messages || []);
      setChatInfo({
        chat_limit: data.chat_limit,
        messages_remaining: data.messages_remaining,
        chat_limit_extensions: data.chat_limit_extensions,
        dispute_status: data.dispute_status,
      });
    } catch { setError('Could not load messages.'); }
    finally { setLoading(false); }
  }, [disputeId]);

  useEffect(() => { fetchMessages(); }, [fetchMessages]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const handleSend = async () => {
    if (!text.trim()) return;
    setSending(true); setError('');
    try {
      const { data } = await apiClient.post(`/api/v1/disputes/${disputeId}/chat/`, { message: text.trim() });
      setMessages(prev => [...prev, data]);
      setChatInfo(prev => ({ ...prev, messages_remaining: data.messages_remaining, chat_limit: data.chat_limit }));
      setText(''); onSent?.();
    } catch (err) { setError(err.response?.data?.error?.message || 'Failed to send message.'); }
    finally { setSending(false); }
  };

  if (loading) return <CircularProgress size={20} sx={{ mt: 1 }} />;
  const limitReached = chatInfo.messages_remaining === 0;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="caption" color="text.secondary">
          Chat thread ({messages.length}/{chatInfo.chat_limit || 10} messages
          {chatInfo.chat_limit_extensions > 0 ? ` · Extended ${chatInfo.chat_limit_extensions}x` : ''})
        </Typography>
        {limitReached && <Chip label="Limit reached" size="small" color="error" variant="outlined" />}
      </Box>
      <Box sx={{ maxHeight: 320, overflowY: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1, mb: 1, bgcolor: 'background.default' }}>
        {messages.length === 0 && <Typography variant="caption" color="text.secondary" sx={{ p: 1, display: 'block' }}>No messages yet.</Typography>}
        {messages.map((m) => {
          const isAdmin = m.sender_role === 'ADMIN';
          return (
            <Box key={m.id} sx={{ display: 'flex', flexDirection: 'column', alignItems: isAdmin ? 'flex-end' : 'flex-start', mb: 1 }}>
              <Box sx={{ maxWidth: '80%', bgcolor: isAdmin ? '#7b1fa2' : 'background.paper', color: isAdmin ? 'white' : 'text.primary', border: isAdmin ? 'none' : '1px solid', borderColor: 'divider', borderRadius: 2, px: 1.5, py: 0.75 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: isAdmin ? 'rgba(255,255,255,0.8)' : (ROLE_COLORS[m.sender_role] || 'text.secondary'), display: 'block', mb: 0.25 }}>
                  {m.sender_role}{isAdmin ? ' (Admin)' : ''}
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{m.message}</Typography>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25, px: 0.5 }}>{new Date(m.sent_at).toLocaleString()}</Typography>
            </Box>
          );
        })}
        <div ref={bottomRef} />
      </Box>
      {chatInfo.dispute_status === 'OPEN' && !limitReached && (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField size="small" fullWidth placeholder={`Send admin message (${chatInfo.messages_remaining} remaining)…`} value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }} inputProps={{ maxLength: 2000 }} multiline maxRows={3} />
          <IconButton color="secondary" onClick={handleSend} disabled={sending || !text.trim()} aria-label="Send admin message">
            {sending ? <CircularProgress size={20} /> : <SendIcon />}
          </IconButton>
        </Box>
      )}
      {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
    </Box>
  );
}

// ─── All Disputes Tab ────────────────────────────────────────────────────────
function AllDisputesTab({ navigate }) {
  const [disputes, setDisputes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [escalatingId, setEscalatingId] = useState(null);
  const [resolveDialog, setResolveDialog] = useState(null);
  const [resolveDecision, setResolveDecision] = useState('APPROVE');
  const [resolving, setResolving] = useState(false);
  const [extending, setExtending] = useState(null);
  const [splitDialog, setSplitDialog] = useState(null);
  const [splitBuyer, setSplitBuyer] = useState('');
  const [splitBidder, setSplitBidder] = useState('');
  const [splitting, setSplitting] = useState(false);
  const [splitError, setSplitError] = useState('');
  const [payingParty, setPayingParty] = useState(null);

  const fetchDisputes = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get('/api/v1/admin/disputes/');
      setDisputes(data.disputes || []);
    } catch (err) { setError(err.response?.data?.error?.message || 'Failed to load disputes.'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchDisputes(); }, [fetchDisputes]);

  const handleOpenArbitration = async (disputeId) => {
    setEscalatingId(disputeId);
    try {
      const { data } = await apiClient.post(`/api/v1/admin/disputes/${disputeId}/open-arbitration/`, {});
      navigate(`/arbitrations/${data.arbitration_id}`);
    } catch (err) { alert(err.response?.data?.error?.message || 'Failed to open arbitration.'); }
    finally { setEscalatingId(null); }
  };

  const handleExtendChat = async (disputeId) => {
    setExtending(disputeId);
    try { await apiClient.post(`/api/v1/admin/disputes/${disputeId}/extend-chat/`); await fetchDisputes(); }
    catch (err) { alert(err.response?.data?.error?.message || 'Failed to extend chat.'); }
    finally { setExtending(null); }
  };

  const handleResolve = async () => {
    if (!resolveDialog) return;
    setResolving(true);
    try { await apiClient.post(`/api/v1/admin/disputes/${resolveDialog.id}/resolve/`, { decision: resolveDecision }); setResolveDialog(null); await fetchDisputes(); }
    catch (err) { alert(err.response?.data?.error?.message || 'Failed to resolve dispute.'); }
    finally { setResolving(false); }
  };

  const handleSplit = async () => {
    if (!splitDialog) return;
    setSplitting(true); setSplitError('');
    try { await apiClient.post(`/api/v1/admin/payment/deals/${splitDialog.deal_id}/dispute-split/`, { buyer_amount: splitBuyer, bidder_amount: splitBidder }); setSplitDialog(null); await fetchDisputes(); }
    catch (err) { setSplitError(err.response?.data?.error?.message || 'Failed to execute split.'); }
    finally { setSplitting(false); }
  };

  const handlePartyPayout = async (dealId, party) => {
    setPayingParty({ dealId, party });
    try { const { data } = await apiClient.post(`/api/v1/admin/payment/deals/${dealId}/dispute-payout/${party}/`); alert(`${party} payout disbursed! Reference: ${data.payout_reference}`); }
    catch (err) { alert(err.response?.data?.error?.message || `Failed to pay ${party}.`); }
    finally { setPayingParty(null); }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (disputes.length === 0) return <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}><Typography color="text.secondary">No disputes found.</Typography></Paper>;

  return (
    <>
      {disputes.map((d) => {
        const isOpen = d.dispute_status === 'OPEN';
        const expanded = expandedId === d.id;
        return (
          <Paper key={d.id} variant="outlined" sx={{ mb: 2, borderColor: isOpen ? 'warning.main' : 'divider' }}>
            <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', cursor: 'pointer' }} onClick={() => setExpandedId(expanded ? null : d.id)}>
              <Typography variant="subtitle1" fontWeight={700}>Dispute #{d.id}</Typography>
              <Chip label={d.dispute_status} size="small" color={DISPUTE_STATUS_COLORS[d.dispute_status] || 'default'} />
              <Chip label={`Deal #${d.deal_id}`} size="small" variant="outlined" />
              {d.milestone_id && <Chip label="Milestone dispute" size="small" variant="outlined" />}
              {d.legal_escalation_recommended && <Chip label="Legal review" size="small" color="error" variant="outlined" />}
              {d.has_active_arbitration && <Chip label="In Arbitration" size="small" color="error" />}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 'auto' }}>
                <ChatBubbleIcon fontSize="small" color="action" />
                <Typography variant="caption" color="text.secondary">{d.message_count}/{d.chat_limit}</Typography>
                {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              </Box>
            </Box>
            <Collapse in={expanded}>
              <Divider />
              <Box sx={{ p: 2 }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 1 }}><strong>Reason:</strong> {d.dispute_reason}</Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                  Opened: {new Date(d.created_at).toLocaleString()}
                  {d.resolved_at ? ` · Resolved: ${new Date(d.resolved_at).toLocaleString()}` : ''}
                </Typography>
                {isOpen && (
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                    <Tooltip title="Add 10 more messages"><Button size="small" variant="outlined" color="info" startIcon={extending === d.id ? <CircularProgress size={14} /> : <ExtensionIcon />} disabled={extending === d.id} onClick={() => handleExtendChat(d.id)}>Extend Chat +10</Button></Tooltip>
                    {d.has_active_arbitration ? (
                      <Button size="small" variant="contained" color="secondary" startIcon={<MeetingRoomIcon />} onClick={() => navigate(`/arbitrations/${d.arbitration_id}`)}>Enter Arbitration Room</Button>
                    ) : (
                      <Button size="small" variant="outlined" color="secondary" startIcon={escalatingId === d.id ? <CircularProgress size={14} /> : <GavelIcon />} disabled={escalatingId !== null} onClick={() => handleOpenArbitration(d.id)}>Open Arbitration Room</Button>
                    )}
                    <Button size="small" variant="contained" color="primary" startIcon={<GavelIcon />} onClick={() => setResolveDialog(d)}>Resolve Dispute</Button>
                    <Button size="small" variant="outlined" color="warning" startIcon={<AccountBalanceIcon />} onClick={() => { setSplitDialog(d); setSplitBuyer(''); setSplitBidder(''); setSplitError(''); }}>Custom Split &amp; Pay</Button>
                    <Button size="small" variant="outlined" disabled={!!payingParty} onClick={() => handlePartyPayout(d.deal_id, 'buyer')}>{payingParty?.dealId === d.deal_id && payingParty?.party === 'buyer' ? <CircularProgress size={16} /> : 'Pay Buyer'}</Button>
                    <Button size="small" variant="outlined" disabled={!!payingParty} onClick={() => handlePartyPayout(d.deal_id, 'bidder')}>{payingParty?.dealId === d.deal_id && payingParty?.party === 'bidder' ? <CircularProgress size={16} /> : 'Pay Bidder'}</Button>
                  </Box>
                )}
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Full Chat Thread (Admin View)</Typography>
                <AdminChatThread disputeId={d.id} onSent={fetchDisputes} />
              </Box>
            </Collapse>
          </Paper>
        );
      })}

      {/* Resolve Dialog */}
      <Dialog open={!!resolveDialog} onClose={() => !resolving && setResolveDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Resolve Dispute #{resolveDialog?.id}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}><strong>Reason:</strong> {resolveDialog?.dispute_reason}</Typography>
          <FormControl fullWidth>
            <InputLabel id="decision-label">Decision</InputLabel>
            <Select labelId="decision-label" value={resolveDecision} label="Decision" onChange={(e) => setResolveDecision(e.target.value)}>
              <MenuItem value="APPROVE">APPROVE (favour the raising party)</MenuItem>
              <MenuItem value="REJECT">REJECT (favour the other party)</MenuItem>
              <MenuItem value="ESCALATE">ESCALATE (legal / further review)</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResolveDialog(null)} disabled={resolving}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={handleResolve} disabled={resolving}>{resolving ? <CircularProgress size={20} color="inherit" /> : 'Confirm Resolution'}</Button>
        </DialogActions>
      </Dialog>

      {/* Split Dialog */}
      <Dialog open={!!splitDialog} onClose={() => !splitting && setSplitDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Custom Dispute Split — Deal #{splitDialog?.deal_id}</DialogTitle>
        <DialogContent>
          {splitError && <Alert severity="error" sx={{ mb: 2 }}>{splitError}</Alert>}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField label="Buyer Refund (Rs.)" type="number" value={splitBuyer} onChange={(e) => setSplitBuyer(e.target.value)} fullWidth inputProps={{ min: 0, step: '0.01' }} />
            <TextField label="Bidder Payout (Rs.)" type="number" value={splitBidder} onChange={(e) => setSplitBidder(e.target.value)} fullWidth inputProps={{ min: 0, step: '0.01' }} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSplitDialog(null)} disabled={splitting}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={handleSplit} disabled={splitting || !splitBuyer || !splitBidder}>{splitting ? <CircularProgress size={20} color="inherit" /> : 'Execute Split'}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

// ─── Escalated Disputes Tab ──────────────────────────────────────────────────
function EscalatedDisputesTab({ navigate }) {
  const [disputes, setDisputes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openingId, setOpeningId] = useState(null);

  const fetchEscalated = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get('/api/v1/admin/disputes/escalated/');
      setDisputes(data.disputes || []);
    } catch (err) { setError(err.response?.data?.error?.message || 'Failed to load escalated disputes.'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchEscalated(); }, [fetchEscalated]);

  const handleOpenArbitration = async (disputeId) => {
    setOpeningId(disputeId);
    try {
      const { data } = await apiClient.post(`/api/v1/admin/disputes/${disputeId}/open-arbitration/`, {});
      navigate(`/arbitrations/${data.arbitration_id}`);
    } catch (err) { alert(err.response?.data?.error?.message || 'Failed to open arbitration room.'); }
    finally { setOpeningId(null); }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (disputes.length === 0) return (
    <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
      <Typography color="text.secondary">No escalated disputes at this time.</Typography>
    </Paper>
  );

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        These disputes have been escalated and require arbitration. Click <strong>Open Arbitration Room</strong> to begin the formal arbitration process.
      </Alert>
      {disputes.map((d) => (
        <Paper key={d.id} variant="outlined" sx={{ mb: 2, borderColor: 'error.main', borderWidth: 1.5 }}>
          <Box sx={{ p: 2, display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ flex: 1, minWidth: 200 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Typography variant="subtitle1" fontWeight={700}>Dispute #{d.id}</Typography>
                <Chip label="ESCALATED" size="small" color="error" />
                <Chip label={`Deal #${d.deal_id}`} size="small" variant="outlined" />
                {d.legal_escalation_recommended && <Chip label="Legal review" size="small" color="error" variant="outlined" />}
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                <strong>Reason:</strong> {d.dispute_reason}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Escalated: {new Date(d.created_at).toLocaleString()}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'flex-end' }}>
              {d.has_active_arbitration ? (
                <Button variant="contained" color="secondary" size="small" startIcon={<MeetingRoomIcon />} onClick={() => navigate(`/arbitrations/${d.arbitration_id}`)}>
                  Enter Arbitration Room #{d.arbitration_id}
                </Button>
              ) : (
                <Button
                  variant="contained" color="error" size="small"
                  startIcon={openingId === d.id ? <CircularProgress size={14} color="inherit" /> : <GavelIcon />}
                  disabled={openingId !== null}
                  onClick={() => handleOpenArbitration(d.id)}
                >
                  Open Arbitration Room
                </Button>
              )}
            </Box>
          </Box>
        </Paper>
      ))}
    </Box>
  );
}

// ─── Arbitration Room List Row (shared by Active + Closed tabs) ──────────────
function ArbitrationRoomRow({ room, navigate, onRefresh, isActive }) {
  const [inviteLoading, setInviteLoading] = useState(false);
  const [assignDialog, setAssignDialog] = useState(false);
  const [arbitratorId, setArbitratorId] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [decisionDialog, setDecisionDialog] = useState(false);
  const [outcome, setOutcome] = useState('RESOLVED');
  const [justification, setJustification] = useState('');
  const [submittingDecision, setSubmittingDecision] = useState(false);

  const [arbitratorsList, setArbitratorsList] = useState([]);
  const [fetchingArbitrators, setFetchingArbitrators] = useState(false);

  const fetchArbitratorsForAssignment = useCallback(async () => {
    setFetchingArbitrators(true);
    try {
      const { data } = await apiClient.get('/api/v1/kyc/admin/arbitrators/');
      const approved = (data.arbitrators || []).filter(a => a.verification_status === 'APPROVED');
      setArbitratorsList(approved);
      if (approved.length > 0 && !arbitratorId) {
        setArbitratorId(String(approved[0].user_id));
      }
    } catch (err) {
      console.error('Failed to load arbitrators list:', err);
    } finally {
      setFetchingArbitrators(false);
    }
  }, [arbitratorId]);

  useEffect(() => {
    if (assignDialog) {
      fetchArbitratorsForAssignment();
    }
  }, [assignDialog, fetchArbitratorsForAssignment]);

  const handleInvite = async (inviteBuyer, inviteBidder) => {
    setInviteLoading(true);
    try {
      await apiClient.post(`/api/v1/admin/arbitrations/${room.id}/invite/`, {
        invite_buyer: inviteBuyer,
        invite_bidder: inviteBidder,
      });
      alert(`Invitation sent to ${inviteBuyer && inviteBidder ? 'Buyer and Bidder' : inviteBuyer ? 'Buyer' : 'Bidder'}.`);
    } catch (err) { alert(err.response?.data?.error?.message || 'Failed to send invitation.'); }
    finally { setInviteLoading(false); }
  };

  const handleAssignArbitrator = async () => {
    if (!arbitratorId.trim()) { alert('Please enter an Arbitrator ID.'); return; }
    setAssigning(true);
    try {
      await apiClient.post(`/api/v1/admin/arbitrations/${room.id}/assign-arbitrator/`, { arbitrator_id: parseInt(arbitratorId, 10) });
      setAssignDialog(false); setArbitratorId(''); onRefresh();
    } catch (err) { alert(err.response?.data?.error?.message || 'Failed to assign arbitrator.'); }
    finally { setAssigning(false); }
  };

  const handleFinalDecision = async () => {
    if (!justification.trim()) { alert('Justification is mandatory.'); return; }
    setSubmittingDecision(true);
    try {
      await apiClient.post(`/api/v1/admin/arbitrations/${room.id}/final-decision/`, { outcome, justification });
      setDecisionDialog(false); onRefresh();
    } catch (err) { alert(err.response?.data?.error?.message || 'Failed to submit decision.'); }
    finally { setSubmittingDecision(false); }
  };

  return (
    <Paper variant="outlined" sx={{ mb: 2, borderColor: isActive ? 'error.main' : 'divider', borderWidth: isActive ? 1.5 : 1 }}>
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 1.5 }}>
          <Typography variant="subtitle1" fontWeight={700}>Room #{room.id}</Typography>
          <Chip label={room.status} size="small" color={ROOM_STATUS_COLORS[room.status] || 'default'} />
          <Chip label={`Dispute #${room.dispute_id}`} size="small" variant="outlined" />
          <Chip label={`Deal #${room.deal_id}`} size="small" variant="outlined" />
          <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
            Opened: {new Date(room.opened_at).toLocaleString()}
            {room.closed_at ? ` · Closed: ${new Date(room.closed_at).toLocaleString()}` : ''}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 1.5 }}>
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">Buyer</Typography>
            <Typography variant="body2">{room.buyer_anonymous_id}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">Bidder</Typography>
            <Typography variant="body2">{room.bidder_anonymous_id}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">Arbitrator</Typography>
            <Typography variant="body2">{room.arbitrator ? `${room.arbitrator.full_name || room.arbitrator.email}` : <em>Not assigned</em>}</Typography>
          </Box>
        </Box>

        {room.decision && (
          <Alert severity={room.decision.decision_type === 'RESOLVE_FOR_BUYER' ? 'success' : 'info'} sx={{ mb: 1.5 }}>
            <strong>Decision:</strong> {room.decision.decision_type} — {room.decision.decision_reason}
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button size="small" variant="contained" color="secondary" startIcon={<MeetingRoomIcon />} onClick={() => navigate(`/arbitrations/${room.id}`)}>
            Enter Room
          </Button>
          {isActive && (
            <>
              <Button size="small" variant="outlined" color="primary" disabled={inviteLoading} onClick={() => handleInvite(true, false)}>
                {inviteLoading ? <CircularProgress size={14} /> : 'Invite Buyer'}
              </Button>
              <Button size="small" variant="outlined" color="primary" disabled={inviteLoading} onClick={() => handleInvite(false, true)}>
                {inviteLoading ? <CircularProgress size={14} /> : 'Invite Bidder'}
              </Button>
              <Button size="small" variant="outlined" color="primary" disabled={inviteLoading} onClick={() => handleInvite(true, true)}>
                {inviteLoading ? <CircularProgress size={14} /> : 'Invite Both'}
              </Button>
              <Button size="small" variant="outlined" color="warning" onClick={() => setAssignDialog(true)}>
                {room.arbitrator ? 'Reassign Arbitrator' : 'Assign Arbitrator'}
              </Button>
              <Button size="small" variant="contained" color="error" startIcon={<GavelIcon />} onClick={() => { setOutcome('RESOLVED'); setJustification(''); setDecisionDialog(true); }}>
                Submit Final Decision
              </Button>
            </>
          )}
        </Box>
      </Box>

      {/* Assign Arbitrator Dialog */}
      <Dialog open={assignDialog} onClose={() => !assigning && setAssignDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Assign Arbitrator to Room #{room.id}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {fetchingArbitrators ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : arbitratorsList.length === 0 ? (
            <Alert severity="warning">No approved arbitrators found. Please approve arbitrators first.</Alert>
          ) : (
            <FormControl fullWidth sx={{ mt: 1 }}>
              <InputLabel id={`select-arbitrator-label-${room.id}`}>Select Arbitrator</InputLabel>
              <Select
                labelId={`select-arbitrator-label-${room.id}`}
                value={arbitratorId}
                label="Select Arbitrator"
                onChange={(e) => setArbitratorId(e.target.value)}
              >
                {arbitratorsList.map((a) => {
                  const statusColors = { ONLINE: '#10b981', BUSY: '#f59e0b', OFFLINE: '#ef4444' };
                  const statusColor = statusColors[a.status] || '#cbd5e1';
                  return (
                    <MenuItem key={a.id} value={String(a.user_id)}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {a.full_name} ({a.email})
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: statusColor }} />
                          <Typography variant="caption" sx={{ fontWeight: 700, color: statusColor }}>
                            {a.status}
                          </Typography>
                          <Chip 
                            label={`${a.active_cases} active`} 
                            size="small" 
                            variant="outlined" 
                            color={a.active_cases >= 3 ? 'error' : 'default'}
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        </Box>
                      </Box>
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialog(false)} disabled={assigning}>Cancel</Button>
          <Button variant="contained" onClick={handleAssignArbitrator} disabled={assigning || !arbitratorId || arbitratorsList.length === 0}>
            {assigning ? <CircularProgress size={20} color="inherit" /> : 'Assign'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Final Decision Dialog */}
      <Dialog open={decisionDialog} onClose={() => !submittingDecision && setDecisionDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Submit Final Decision — Room #{room.id}</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>This action will close the arbitration room and update the dispute status. It cannot be undone.</Alert>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Outcome</InputLabel>
            <Select value={outcome} label="Outcome" onChange={(e) => setOutcome(e.target.value)}>
              <MenuItem value="RESOLVED">RESOLVED — Both parties reached agreement</MenuItem>
              <MenuItem value="UNRESOLVED">UNRESOLVED — No agreement could be reached</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Justification (mandatory)" fullWidth multiline rows={4}
            value={justification} onChange={(e) => setJustification(e.target.value)}
            placeholder={outcome === 'RESOLVED' ? 'e.g. Both parties reached a mutual agreement after arbitration.' : 'e.g. No agreement could be reached after arbitration review.'}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDecisionDialog(false)} disabled={submittingDecision}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleFinalDecision} disabled={submittingDecision || !justification.trim()}>
            {submittingDecision ? <CircularProgress size={20} color="inherit" /> : 'Confirm Decision'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

// ─── Active Arbitration Rooms Tab ────────────────────────────────────────────
function ActiveArbitrationTab({ navigate }) {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchRooms = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get('/api/v1/admin/arbitrations/active/');
      setRooms(data.rooms || []);
    } catch (err) { setError(err.response?.data?.error?.message || 'Failed to load active rooms.'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchRooms(); }, [fetchRooms]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (rooms.length === 0) return (
    <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
      <Typography color="text.secondary">No active arbitration rooms.</Typography>
    </Paper>
  );

  return (
    <Box>
      <Alert severity="warning" sx={{ mb: 2 }}>
        {rooms.length} active arbitration room{rooms.length !== 1 ? 's' : ''} require attention.
      </Alert>
      {rooms.map((r) => (
        <ArbitrationRoomRow key={r.id} room={r} navigate={navigate} onRefresh={fetchRooms} isActive={true} />
      ))}
    </Box>
  );
}

// ─── Closed Arbitration Rooms Tab ────────────────────────────────────────────
function ClosedArbitrationTab({ navigate }) {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchRooms = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get('/api/v1/admin/arbitrations/closed/');
      setRooms(data.rooms || []);
    } catch (err) { setError(err.response?.data?.error?.message || 'Failed to load closed rooms.'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchRooms(); }, [fetchRooms]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (rooms.length === 0) return (
    <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
      <Typography color="text.secondary">No closed arbitration rooms yet.</Typography>
    </Paper>
  );

  return (
    <Box>
      {rooms.map((r) => (
        <ArbitrationRoomRow key={r.id} room={r} navigate={navigate} onRefresh={fetchRooms} isActive={false} />
      ))}
    </Box>
  );
}

// ─── Arbitrator Availability & Status Tab ────────────────────────────────────
function ArbitratorAvailabilityTab() {
  const [arbitrators, setArbitrators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchArbitrators = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get('/api/v1/kyc/admin/arbitrators/');
      setArbitrators(data.arbitrators || []);
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to load arbitrators.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchArbitrators();
  }, [fetchArbitrators]);

  const formatSchedule = (schedule) => {
    if (!schedule || !schedule.working_days || schedule.working_days.length === 0) {
      return 'No schedule set';
    }
    const days = schedule.working_days.map(d => d.substring(0, 3)).join(', ');
    const hours = schedule.start_time && schedule.end_time 
      ? ` (${schedule.start_time} - ${schedule.end_time})`
      : '';
    return `${days}${hours}`;
  };

  const getComputedStatusChip = (status) => {
    switch (status) {
      case 'ONLINE':
        return <Chip label="ONLINE" size="small" variant="outlined" sx={{ fontWeight: 700, borderColor: '#10b981', color: '#10b981', bgcolor: 'rgba(16,185,129,0.05)' }} />;
      case 'BUSY':
        return <Chip label="BUSY" size="small" variant="outlined" sx={{ fontWeight: 700, borderColor: '#f59e0b', color: '#f59e0b', bgcolor: 'rgba(245,158,11,0.05)' }} />;
      default:
        return <Chip label="OFFLINE" size="small" variant="outlined" sx={{ fontWeight: 700, borderColor: '#ef4444', color: '#ef4444', bgcolor: 'rgba(239,68,68,0.05)' }} />;
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (arbitrators.length === 0) return <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}><Typography color="text.secondary">No arbitrators found.</Typography></Paper>;

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 3 }}>
        Arbitrators listed below have submitted profiles and been verified. Monitor their live statuses, current active workloads, and working schedules.
      </Alert>
      <TableContainer component={Paper} variant="outlined">
        <Table aria-label="arbitrators table">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Arbitrator ID</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Name / Email</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Live Status</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Active Cases Workload</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Working Schedule</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Last Login</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {arbitrators.map((p) => (
              <TableRow key={p.id}>
                <TableCell>{p.user_id}</TableCell>
                <TableCell>
                  <Typography fontWeight={600} variant="body2">{p.full_name}</Typography>
                  <Typography variant="caption" color="text.secondary">{p.email}</Typography>
                </TableCell>
                <TableCell>{getComputedStatusChip(p.status)}</TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight={700} color={p.active_cases >= 3 ? 'error' : 'text.primary'}>
                    {p.active_cases} Active (max 3)
                  </Typography>
                </TableCell>
                <TableCell>{formatSchedule(p.working_schedule)}</TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary">
                    {p.last_login ? new Date(p.last_login).toLocaleString() : 'Never'}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

// ─── Main Export ─────────────────────────────────────────────────────────────
export default function AdminDisputes() {
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);

  useEffect(() => {
    const role = localStorage.getItem('cmp_role');
    if (role !== 'ADMIN') {
      if (role === 'ARBITRATOR') {
        navigate('/arbitrations/mine');
      } else if (role === 'BUYER') {
        navigate('/buyer/requirements');
      } else if (role === 'BIDDER') {
        navigate('/bidder/requirements');
      } else {
        navigate('/login');
      }
    }
  }, [navigate]);

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', mt: 4, p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Admin — Dispute Services</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Manage all disputes, escalated cases, and arbitration rooms from this panel.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button
            variant="outlined" color="primary"
            onClick={() => navigate('/admin/deals')}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Manage Deals
          </Button>
          <Button
            variant="contained" color="secondary"
            onClick={() => navigate('/admin/arbitrators')}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Arbitrators Review
          </Button>
        </Box>
      </Box>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
        variant="scrollable"
        scrollButtons="auto"
      >
        <Tab label="All Disputes" />
        <Tab label="Escalated Disputes" />
        <Tab label="Active Arbitration Rooms" />
        <Tab label="Closed Arbitration Rooms" />
        <Tab label="Arbitrator Availability" />
      </Tabs>

      {tab === 0 && <AllDisputesTab navigate={navigate} />}
      {tab === 1 && <EscalatedDisputesTab navigate={navigate} />}
      {tab === 2 && <ActiveArbitrationTab navigate={navigate} />}
      {tab === 3 && <ClosedArbitrationTab navigate={navigate} />}
      {tab === 4 && <ArbitratorAvailabilityTab />}
    </Box>
  );
}
